import ExcelJS, { type Style, type Workbook, type Worksheet } from 'exceljs';
import { downloadFile } from 'src/utils/fileTransformation';

type ShoppingListCellStyle = Partial<Pick<Style, 'font' | 'fill' | 'alignment' | 'border'>>;

const BLACK = { argb: 'FF000000' };
const HEADER_FILL = { argb: 'FF93ADD3' };

const THIN_BORDER = {
  style: 'thin' as const,
  color: BLACK,
};

const TABLE_BORDER = {
  top: THIN_BORDER,
  bottom: THIN_BORDER,
  left: THIN_BORDER,
  right: THIN_BORDER,
};

export const SHOPPING_LIST_EXCEL_STYLES = {
  title: {
    font: { bold: true, size: 11, color: BLACK },
  },
  date: {
    font: { bold: true, size: 11, color: BLACK },
  },
  header: {
    font: { bold: true, size: 9, color: BLACK },
    fill: { type: 'pattern' as const, pattern: 'solid' as const, fgColor: HEADER_FILL },
    alignment: { horizontal: 'left' as const, vertical: 'middle' as const },
    border: TABLE_BORDER,
  },
  data: {
    font: { size: 9, color: BLACK },
    alignment: { horizontal: 'left' as const, vertical: 'middle' as const },
    border: TABLE_BORDER,
  },
  empty: {
    font: { size: 9, color: BLACK },
    alignment: { horizontal: 'left' as const, vertical: 'middle' as const },
    border: TABLE_BORDER,
  },
  total: {
    font: { bold: true, size: 9, color: BLACK },
    alignment: { horizontal: 'left' as const, vertical: 'middle' as const },
    border: TABLE_BORDER,
  },
} satisfies Record<string, ShoppingListCellStyle>;

export interface ShoppingListStyledExportRow<TKind extends string = string> {
  values: (string | number)[];
  kind: TKind;
}

export interface BuildShoppingListExcelWorksheetOptions<TKind extends string> {
  columnCount: number;
  columnWidths: readonly number[];
  isFullWidthMergedRow: (kind: TKind) => boolean;
  styleForKind: (kind: TKind) => ShoppingListCellStyle | null;
}

export const applyShoppingListExportPageSetup = (worksheet: Worksheet): void => {
  worksheet.pageSetup = {
    orientation: 'landscape',
    fitToPage: true,
    fitToWidth: 1,
    fitToHeight: 0,
    horizontalCentered: false,
    verticalCentered: false,
    margins: {
      left: 0.25,
      right: 0.25,
      top: 0.5,
      bottom: 0.5,
      header: 0.3,
      footer: 0.3,
    },
  };
};

const applyCellStyle = (cell: ExcelJS.Cell, style: ShoppingListCellStyle): void => {
  if (style.font) {
    cell.font = style.font;
  }
  if (style.fill) {
    cell.fill = style.fill;
  }
  if (style.alignment) {
    cell.alignment = style.alignment;
  }
  if (style.border) {
    cell.border = style.border;
  }
};

export const buildShoppingListExcelWorksheet = <TKind extends string>(
  workbook: Workbook,
  sheetName: string,
  styledRows: ShoppingListStyledExportRow<TKind>[],
  options: BuildShoppingListExcelWorksheetOptions<TKind>
): Worksheet => {
  const { columnCount, columnWidths, isFullWidthMergedRow, styleForKind } = options;
  const worksheet = workbook.addWorksheet(sheetName);

  columnWidths.forEach((width, index) => {
    worksheet.getColumn(index + 1).width = width;
  });

  applyShoppingListExportPageSetup(worksheet);

  styledRows.forEach((row, rowIndex) => {
    const excelRow = worksheet.getRow(rowIndex + 1);
    const style = styleForKind(row.kind);

    if (isFullWidthMergedRow(row.kind)) {
      excelRow.getCell(1).value = row.values[0] ?? '';
      for (let columnIndex = 1; columnIndex < columnCount; columnIndex += 1) {
        excelRow.getCell(columnIndex + 1).value = '';
      }
      worksheet.mergeCells(rowIndex + 1, 1, rowIndex + 1, columnCount);
    } else if (row.kind === 'header' || row.kind === 'data') {
      row.values.forEach((value, columnIndex) => {
        excelRow.getCell(columnIndex + 1).value = value;
      });
    } else if (row.values.length > 0) {
      excelRow.getCell(1).value = row.values[0] ?? '';
    }

    if (!style) {
      return;
    }

    const styledColumnCount =
      row.kind === 'header' || row.kind === 'data' || isFullWidthMergedRow(row.kind)
        ? columnCount
        : Math.max(row.values.length, 1);

    for (let columnIndex = 0; columnIndex < styledColumnCount; columnIndex += 1) {
      applyCellStyle(excelRow.getCell(columnIndex + 1), style);
    }
  });

  return worksheet;
};

export const downloadExcelWorkbook = async (
  workbook: Workbook,
  fileName: string
): Promise<void> => {
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = window.URL.createObjectURL(blob);
  downloadFile(url, `${fileName}.xlsx`);
};
